import {
  Component,
  ComponentRef,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  Type,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  viewChild
} from '@angular/core';
import { ComponentRenderer } from '../../renderers/component-renderer';
import { DefaultFilterLabelRendererComponent } from './default-filter-label.component';
import { FilterRenderer } from './filter-renderer';
import { Filter } from '../filters';

import { AbstractControl } from '@angular/forms';

/**
 * Component that dynamically renders configured CellRenderer components to Columns
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'cbre-filter-label-renderer',
  template: `<ng-container #Container></ng-container> `,
  imports: [],
  encapsulation: ViewEncapsulation.None,
})
export class FilterLabelRendererComponent<T extends Filter> implements OnInit, ComponentRenderer<FilterRenderer<T>> {
  readonly filter = input<T>();
  readonly form = input<AbstractControl>();

  /**
   * ContainerRef to render the configured CellRenderer<T> type
   */
  private readonly container = viewChild('Container', { read: ViewContainerRef });

  public input: ComponentRef<FilterRenderer<T>>;

  private readonly parentInjector = inject(Injector);

  constructor() {
    effect(() => {
      const cfg = this.filter();
      if (cfg != null && this.input) {
        this.input.setInput('filter', cfg);
      }
    });
    effect(() => {
      const form = this.form();
      if (form != null && this.input) {
        this.input.setInput('form', form);
      }
    });
  }

  ngOnInit(): void {
    const rendererType: Type<FilterRenderer<any>> = this.resolveComponentType();
    this.mountComponent(rendererType);
  }

  public resolveComponentType(): Type<FilterRenderer<any>> {
    return this.filter()?.LabelRenderer ?? this.getDefaultComponent();
  }

  public getDefaultComponent(): Type<FilterRenderer<any>> {
    return DefaultFilterLabelRendererComponent;
  }

  public mountComponent(component: Type<FilterRenderer<T>>) {
    this.input = this.container().createComponent(component, { injector: this.parentInjector });
    this.input.setInput('filter', this.filter());
    this.input.setInput('form', this.form());
  }
}
